// Considered: ArcActionExecutionListMolecule — rejected because that renders runtime execution
// status of actions that already ran; this card is a declarative statement of trigger + verb +
// subject + trust for an action still being authored. MessageAtom was also considered and
// rejected — a notice, not a selectable, tier-chipped card. See spec Reuse Audit
// (arc-editor-canvas-v1.md).
import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import './ArcActionCardMolecule.scss';

export interface ArcActionCardHook {
  /** Iconify `lucide:*` name (`AC_HOOKS[hook].glyph`). */
  glyph: string;
  /** Already-localized hook word (`AC_HOOKS[hook].word`). */
  word: string;
}

export interface ArcActionCardTier {
  /** Already-localized tier label (`acTier(tier).label`), lowercased. */
  label: string;
  /** `suggest|draft|confirm` hold for a person vs. `delegated|automatic` (`acHoldsForPerson`). */
  holdsForPerson: boolean;
}

/** Where this action's definition comes from — quiet (omitted by the caller) when the arc owns
 * it, present when it travels here from an approach, a channel, or an inherited template
 * (`ProvChip`, `scope-data.jsx:116-126`). Already-resolved and already-localized, like every
 * other field on `ArcActionCardAction`. */
export interface ArcActionCardProvenance {
  label: string;
  /** Iconify `lucide:*` name. */
  icon?: string;
}

export interface ArcActionCardAction {
  id: string;
  hook: ArcActionCardHook;
  /** Already-localized action type label (`acActionType(type).label`), lowercased. */
  verb: string;
  subject: string;
  tier: ArcActionCardTier;
  provenance?: ArcActionCardProvenance;
  selected?: boolean;
}

export interface ArcActionCardMoleculeProps {
  actions: ArcActionCardAction[];
  editable?: boolean;
  onSelect: (action: ArcActionCardAction) => void;
  onAdd: () => void;
  className?: string;
}

/** The action lane cell for one stage column: one card per automatic action (hook, verb +
 * subject, tier chip) or a "nothing automatic here" hint, plus an add-action mini button
 * (`arc-lane.jsx:155-181`). */
const ArcActionCardMolecule: React.FC<ArcActionCardMoleculeProps> = ({
  actions,
  editable = false,
  onSelect,
  onAdd,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`arc-action-card ${className}`.trim()}
      data-lane="auto"
      data-container="each(actions)"
    >
      {actions.length === 0 && (
        <span
          className="arc-action-card__none"
          data-datum="canvas.nothing-automatic-here"
          data-kind="hint"
        >
          {t('studio.arcEditor.actionCard.nothingAutomaticHere')}
        </span>
      )}
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          className="arc-action-card__card"
          data-selected={action.selected ? 'true' : undefined}
          data-datum="canvas.card#4"
          data-kind="card"
          onClick={() => onSelect(action)}
        >
          <span className="arc-action-card__hook" data-datum="canvas.hook-word" data-kind="label">
            <GlyphAtom glyph={`lucide:${action.hook.glyph}`} aria-hidden />
            {action.hook.word}
          </span>
          <span className="arc-action-card__verb">
            <span data-datum="canvas.x#2" data-kind="value">
              {action.verb}
            </span>{' '}
            <b data-datum="canvas.x#3" data-kind="strong">
              {action.subject || t('studio.arcEditor.actionCard.noSubject')}
            </b>
          </span>
          <span className="arc-action-card__chips">
            <TagAtom
              label={action.tier.label}
              tone={action.tier.holdsForPerson ? 'warn' : undefined}
              icon={action.tier.holdsForPerson ? 'lucide:hand' : 'lucide:zap'}
              size="small"
              data-datum="canvas.chip#4"
              data-kind="chip"
            />
            {action.provenance && (
              <TagAtom
                label={action.provenance.label}
                icon={action.provenance.icon}
                size="small"
                data-datum="canvas.chip#6"
                data-kind="chip"
              />
            )}
          </span>
        </button>
      ))}
      {editable && (
        <ButtonAtom
          variant="secondary"
          size="small"
          icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
          label={t('studio.arcEditor.actionCard.addAction')}
          onClick={onAdd}
          data-datum="canvas.action"
          data-kind="button"
        />
      )}
    </div>
  );
};

ArcActionCardMolecule.displayName = 'ArcActionCardMolecule';

export default ArcActionCardMolecule;
